import type { CollectionProps } from '@react-aria/collections';
import React from 'react';

import { Group } from '../Group';
import { Heading } from '../Heading';
import {
    type Section,
    type SectionRenderItemInfo,
    SectionsList,
    type SectionsListSelectionProps,
} from './SectionsList';

export interface TimelineListProps<Item extends object>
    extends SectionsListSelectionProps<Item>,
        Pick<CollectionProps<Item>, 'dependencies'> {
    data: Item[];
    getItemTime: (item: Item) => Date;
    getItemId: (item: Item) => string;
    renderItem: (info: SectionRenderItemInfo<Item>) => React.ReactElement;
}

export function TimelineList<Item extends object>(props: TimelineListProps<Item>) {
    const { data, getItemTime, getItemId, renderItem, ...rest } = props;

    const sections = React.useMemo(() => groupItemsByTime(data, getItemTime), [data, getItemTime]);

    const getSectionItemKey = (item: Item) => {
        return `${getItemTime(item).toISOString()}-${getItemId(item)}`;
    };

    return (
        <SectionsList
            {...rest}
            items={sections}
            getSectionItemKey={getSectionItemKey}
            renderSectionItem={renderItem}
            renderSectionHeader={({ section }) => (
                <TimelineSectionHeader sectionKey={section.key} />
            )}
        />
    );
}

function groupItemsByTime<Item extends object>(
    items: Item[],
    getTime: (item: Item) => Date
): Section<Item>[] {
    const sections: Section<Item>[] = [];

    const sectionsMap = new Map<string, Item[]>();
    items.forEach((item) => {
        const date = getTime(item);
        const key = getSectionKey(date);
        sectionsMap.set(key, [...(sectionsMap.get(key) || []), item]);
    });

    sectionsMap.forEach((items, key) => {
        sections.push({ key, items });
    });

    return sections;
}

function getSectionKey(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

function TimelineSectionHeader({ sectionKey }: { sectionKey: string }) {
    const sectionDate = new Date(sectionKey);

    return (
        <Group className="px-3 py-2">
            <Heading level={4}>{getTimelineSectionDateLabel(sectionDate)}</Heading>
        </Group>
    );
}

function getTimelineSectionDateLabel(date: Date): string {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const msPerDay = 1000 * 60 * 60 * 24;
    const diffInDays = Math.round((startOfToday.getTime() - startOfDate.getTime()) / msPerDay);

    if (diffInDays === 0) {
        return 'Today';
    }
    if (diffInDays === 1) {
        return 'Yesterday';
    }

    return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    }).format(date);
}
